import React, {
  useMemo,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react'
import { useLocalStorage } from 'react-use'
import { GridsAndLayoutsByDocument } from './types'
import {
  GRIDS_AND_LAYOUT_LOCAL_STORAGE_KEY,
  LOCAL_STORAGE_MAX_DOCUMENTS,
  NUMBER_DOCS_TO_REMOVE,
} from './constants'

export type GridsAndLayoutsContext = {
  gridsAndLayoutsByDocument: GridsAndLayoutsByDocument | undefined
  setGridsAndLayoutsByDocument: Dispatch<
    SetStateAction<GridsAndLayoutsByDocument | undefined>
  >
}

export const gridsAndLayoutsContext = createContext<
  GridsAndLayoutsContext | undefined
>(undefined)

type GridsAndLayoutsProviderProps = {
  children: React.ReactNode
}

/**
 * Provide the state (synced with local storage) of users’ custom grid and layout settings for frames.
 * @see GridsAndLayoutsByDocument for the structure of the state.
 */
export function GridsAndLayoutsProvider({
  children,
}: GridsAndLayoutsProviderProps) {
  // We’re using a context to share state across components because using the useLocalStorage
  // hook independently in multiple components does not maintain a shared state,
  // each hook call creates its own instance and they do not synchronize with local storage changes.
  const [gridsAndLayoutsLocalStorage, setGridsAndLayoutsLocalStorage] =
    useLocalStorage(
      GRIDS_AND_LAYOUT_LOCAL_STORAGE_KEY,
      {} as GridsAndLayoutsByDocument
    )

  const contextValue = useMemo((): GridsAndLayoutsContext => {
    return {
      gridsAndLayoutsByDocument: gridsAndLayoutsLocalStorage,
      setGridsAndLayoutsByDocument: setGridsAndLayoutsLocalStorage,
    }
  }, [gridsAndLayoutsLocalStorage, setGridsAndLayoutsLocalStorage])

  useEffect(() => {
    const cleanedUpgGridsAndLayoutsState = getCleanedUpStateIfNeeded(
      gridsAndLayoutsLocalStorage
    )

    if (cleanedUpgGridsAndLayoutsState) {
      setGridsAndLayoutsLocalStorage(cleanedUpgGridsAndLayoutsState)
    }
  }, [gridsAndLayoutsLocalStorage, setGridsAndLayoutsLocalStorage])

  return (
    <gridsAndLayoutsContext.Provider value={contextValue}>
      {children}
    </gridsAndLayoutsContext.Provider>
  )
}

export function useGridsAndLayoutsContext() {
  const value = useContext(gridsAndLayoutsContext)

  if (typeof value === 'undefined') {
    throw new Error(
      'useGridsAndLayoutsContext must be used within an GridsAndLayoutsProvider component'
    )
  }

  return value
}

/**
 * One off clean up of the local storage state to remove old entries to prevent local storage from growing indefinitely.
 */
export function getCleanedUpStateIfNeeded(
  gridsAndLayoutsLocalStorage: GridsAndLayoutsByDocument | undefined
) {
  if (
    gridsAndLayoutsLocalStorage &&
    Object.keys(gridsAndLayoutsLocalStorage).length >=
      LOCAL_STORAGE_MAX_DOCUMENTS
  ) {
    const docIdSortedByOldestFirst = Object.keys(
      gridsAndLayoutsLocalStorage
    ).sort((keyA, keyB) => {
      return (
        gridsAndLayoutsLocalStorage[keyA].lastUpdated -
        gridsAndLayoutsLocalStorage[keyB].lastUpdated
      )
    })

    const entriesToRemove = docIdSortedByOldestFirst.slice(
      0,
      NUMBER_DOCS_TO_REMOVE
    )

    const newGridsAndLayouts: GridsAndLayoutsByDocument = {
      ...gridsAndLayoutsLocalStorage,
    }

    for (const entry of entriesToRemove) {
      delete newGridsAndLayouts[entry]
    }

    return newGridsAndLayouts
  }

  return null
}
